import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ProtectedRoute, { SignInRoute } from "./ProtectedRoute";
import { USER_ROLES } from "./userRoles";
import IdleTimerWrapper from "../components/IdleTimerWrapper/IdleTimerWrapper";
import SignIn from "../pages/Auth/SignIn/SignIn";
import AuthCallback from "../pages/Auth/AuthCallback/AuthCallback";
import Registration from "../pages/Auth/Registration/Registration";
import ForgotPassword from "../pages/HealthCare/ForgotPassword/ForgotPassword";
import ResetPassword from "../pages/HealthCare/ResetPassword/ResetPassword";
import VerificationCode from "../pages/HealthCare/VerificationCode/VerificationCode";
import HPTable from "../pages/SuperAdmin/HPTable/HPTable";
import AddDevice from "../pages/SuperAdmin/AddDevice/AddDevice";
import DeviceJsonDetails from "../pages/SuperAdmin/DeviceJsonDetails/DeviceJsonDetails";
import AppVersions from "../pages/SuperAdmin/AppVersions/AppVersions";
import HPForm from "../pages/SuperAdmin/HPForm/HPForm";
import PatientTable from "../pages/HealthCare/PatientTable/PatientTable";
import Patient from "../pages/HealthCare/Patient/Patient";
import PatientSessionDetails from "../pages/HealthCare/PatientSessionDetails/PatientSessionDetails";
import AdminDashboard from "../pages/SuperAdmin/AdminDashboard/AdminDashboard";
import HPDashboard from "../pages/HealthCare/HPDashboard/HPDashboard";
import ProfileDetails from "../pages/HealthCare/ProfileDetails/ProfileDetails";
import UserForm from "../pages/HealthCare/UserForm/UserForm";
import SuperAdminEditProfile from "../pages/SuperAdmin/SuperAdminEditProfile/SuperAdminEditProfile";
import SessionGraph from "../pages/HealthCare/SessionGraph/SessionGraph";
import ApiKeyList from "../pages/SuperAdmin/ApiKeyList/ApiKeyList";
import AdminPatients from "../pages/SuperAdmin/AdminPatients/AdminPatients";
import AdminPatientsSession from "../pages/SuperAdmin/AdminPatientsSession/AdminPatientsSession";
import Logs from "../pages/SuperAdmin/Logs/Logs";
import NewLayout from "../components/NewLayout/NewLayout";
import HPLogs from "../pages/HealthCare/HPLogs/HPLogs";
import UserActivityLogsPage from "../pages/SuperAdmin/UserActivityLogsPage/UserActivityLogsPage";
import DeviceActivityLogsPage from "../pages/SuperAdmin/DeviceActivityLogsPage/DeviceActivityLogsPage";
import HPUserActivityLogsPage from "../pages/HealthCare/HPUserActivityLogsPage/HPUserActivityLogsPage";
import HPDeviceActivityLogsPage from "../pages/HealthCare/HPDeviceActivityLogsPage/HPDeviceActivityLogsPage";
import OtpVerification from "../pages/HealthCare/OtpVerification/OtpVerification";
import HealthCareDevices from "../pages/HealthCare/HealthCareDevices/HealthCareDevices";
import SurveysListPage from "../pages/HealthCare/Surveys/SurveysListPage";
import SurveyBuilderPage from "../pages/HealthCare/Surveys/SurveyBuilderPage";
import SurveyResultsListPage from "../pages/HealthCare/Surveys/SurveyResultsListPage";
import SurveyResultDetailPage from "../pages/HealthCare/Surveys/SurveyResultDetailPage";

const superAdminRoles = [USER_ROLES.SUPER_ADMIN];
const healthProfessionalRoles = [USER_ROLES.HEALTH_PROFESSIONAL];
const authenticatedRoles = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.HEALTH_PROFESSIONAL,
];

const protectedRoutes = [
  // Super-Admin pages
  { path: "/admin-dashboard", roles: superAdminRoles, redirectTo: "/super-admin", Component: AdminDashboard },
  { path: "/hptable", roles: superAdminRoles, redirectTo: "/super-admin", Component: HPTable },
  { path: "/apikeylist", roles: superAdminRoles, redirectTo: "/super-admin", Component: ApiKeyList },
  { path: "/hpform", roles: superAdminRoles, redirectTo: "/super-admin", Component: HPForm },
  { path: "/add-device", roles: superAdminRoles, redirectTo: "/super-admin", Component: AddDevice },
  { path: "/device-data/:deviceId", roles: superAdminRoles, redirectTo: "/super-admin", Component: DeviceJsonDetails },
  { path: "/superadmin-patients/:hpId", roles: superAdminRoles, redirectTo: "/super-admin", Component: AdminPatients },
  { path: "/superadmin-session", roles: superAdminRoles, redirectTo: "/super-admin", Component: AdminPatientsSession },
  { path: "/superadmin-graph", roles: superAdminRoles, redirectTo: "/super-admin", Component: SessionGraph },
  { path: "/logs", roles: superAdminRoles, redirectTo: "/super-admin", Component: Logs },
  { path: "/logs/user-activity/:userId", roles: superAdminRoles, redirectTo: "/super-admin", Component: UserActivityLogsPage },
  { path: "/logs/device-activity/:deviceName", roles: superAdminRoles, redirectTo: "/super-admin", Component: DeviceActivityLogsPage },
  { path: "/superadmin-edit", roles: superAdminRoles, redirectTo: "/super-admin", Component: SuperAdminEditProfile },
  { path: "/app-versions", roles: superAdminRoles, redirectTo: "/super-admin", Component: AppVersions },
  // Health-Professional pages
  { path: "/hp-dashboard", roles: healthProfessionalRoles, Component: HPDashboard },
  { path: "/patient-logs", roles: healthProfessionalRoles, Component: HPLogs },
  { path: "/user-form", roles: healthProfessionalRoles, Component: UserForm },
  { path: "/patient-view", roles: healthProfessionalRoles, Component: PatientSessionDetails },
  { path: "/patient-table", roles: healthProfessionalRoles, Component: PatientTable },
  { path: "/patient-form", roles: healthProfessionalRoles, Component: Patient },
  { path: "/session-graph", roles: healthProfessionalRoles, Component: SessionGraph },
  { path: "/user-activity-logs/:userId", roles: healthProfessionalRoles, Component: HPUserActivityLogsPage },
  { path: "/device-activity-logs/:deviceName", roles: healthProfessionalRoles, Component: HPDeviceActivityLogsPage },
  { path: "/my-devices", roles: healthProfessionalRoles, Component: HealthCareDevices },
  { path: "/surveys", roles: healthProfessionalRoles, Component: SurveysListPage },
  { path: "/survey-form", roles: healthProfessionalRoles, Component: SurveyBuilderPage },
  { path: "/survey-results", roles: healthProfessionalRoles, Component: SurveyResultsListPage },
  { path: "/survey-results-view", roles: healthProfessionalRoles, Component: SurveyResultDetailPage },
  // Shared
  { path: "/profile-details", roles: authenticatedRoles, Component: ProfileDetails },
];

const AppRoutes = () => (
  <Routes>
    <Route element={<NewLayout />}>
      {protectedRoutes.map(({ path, roles, redirectTo, Component }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute allowedRoles={roles} redirectTo={redirectTo}>
              <Component />
            </ProtectedRoute>
          }
        />
      ))}
    </Route>
    {/* Unknown path under "/" (mistyped/stale URL) — send back to the root. */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);
// Collapses accidental repeated slashes (//super-admin, /super-admin//, ...)
// before any Route below gets to match. Doing this as an effect raced the
// nested "*" -> Navigate("/") fallback (both fire in the same commit, so
// whichever ran last won) and mistyped super-admin URLs lost to it, landing
// on "/" instead of "/super-admin". Rendering the redirect directly here,
// in place of the routes tree, removes the second navigate() entirely.
const RouterGate = () => {
  const location = useLocation();
  const normalizedPathname = location.pathname.replace(/\/{2,}/g, "/");

  if (normalizedPathname !== location.pathname) {
    return <Navigate to={`${normalizedPathname}${location.search}${location.hash}`} replace />;
  }

  return (
    <>
      <IdleTimerWrapper />
      <Routes>
        <Route path="/*" element={<AppRoutes />} />
        <Route path="/otp-verification" element={<OtpVerification />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/super-admin" element={<SignInRoute><SignIn loginType={1} /></SignInRoute>} />
        <Route path="/" element={<SignInRoute><SignIn loginType={2} /></SignInRoute>} />
        <Route path="/register" element={<SignInRoute><Registration /></SignInRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verification-code" element={<VerificationCode />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    </>
  );
};

const Routercomponent = () => {
  return (
    <BrowserRouter>
      <RouterGate />
    </BrowserRouter>
  );
};

export default Routercomponent;
